import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { from } from "rxjs";
import { exhaustMap, map, catchError, withLatestFrom } from "rxjs/operators";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
  AppState,
  loadedProfessionalFuelCustomerState,
  loadedProfessionalFuelDatesState,
} from "../../reducers";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import * as ProfessionalFuelActionTypes from "../../actions/professional-fuel/professional-fuel.actions";

import { ProfessionalFuelService } from "../../../shared/services/professional-fuel/professional-fuel.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";

@Injectable()
export class ProfessionalFuelEffects {
  // Professional Fuel PT
  getProfessionalFuelPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfessionalFuelActionTypes.getProfessionalFuelData),
      exhaustMap((action) =>
        this.professionalFuelService
          .getProfessionalFuel(
            "pt",
            action.dates,
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProfessionalFuelActionTypes.loadProfessionalFuelData({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProfessionalFuelActionTypes.setLoadingProfessionalEnd(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProfessionalFuel.ProfessionalFuelLoadingError"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getGenerateProfessionalFuel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfessionalFuelActionTypes.getGenerateProfessionalFuelData),
      withLatestFrom(this.store.select(loadedProfessionalFuelDatesState)),
      exhaustMap(([action, dates]) =>
        this.professionalFuelService
          .getGenerateProfessionalFuel("pt", dates)
          .pipe(
            map((result) => {
              return ProfessionalFuelActionTypes.loadGenerateProfessionalFuelData(
                { response: result }
              );
            }),
            catchError((error) => {
              const actions = [
                ProfessionalFuelActionTypes.setLoadingProfessionalEnd(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProfessionalFuel.errorGenerateFile"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Professional Fuel ES
  getProfessionalFuelEsPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfessionalFuelActionTypes.getProfessionalFuelEsData),
      exhaustMap((action) =>
        this.professionalFuelService
          .getProfessionalFuel(
            "es",
            action.dates,
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs,
            action.partyCode
          )
          .pipe(
            map((result) =>
              ProfessionalFuelActionTypes.loadProfessionalFuelData({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProfessionalFuelActionTypes.setLoadingProfessionalEnd(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProfessionalFuel.ProfessionalFuelLoadingError"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getGenerateProfessionalFuelEs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfessionalFuelActionTypes.getGenerateProfessionalFuelEsData),
      withLatestFrom(
        this.store.select(loadedProfessionalFuelDatesState),
        this.store.select(loadedProfessionalFuelCustomerState)
      ),
      exhaustMap(([action, dates, partyCode]) =>
        this.professionalFuelService
          .getGenerateProfessionalFuelEs(dates, partyCode)
          .pipe(
            map((result) => {
              return ProfessionalFuelActionTypes.loadGenerateProfessionalFuelData(
                { response: result }
              );
            }),
            catchError((error) => {
              const actions = [
                ProfessionalFuelActionTypes.setLoadingProfessionalEnd(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProfessionalFuel.errorGenerateFile"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private professionalFuelService: ProfessionalFuelService,
    private translate: TranslateService,
    private store: Store<AppState>
  ) {}
}
